import { trackPageView } from '@finn-no/pulse-sdk';
import { Breadcrumbs } from '@warp-ds/react/components/breadcrumbs';
import { type PropsWithChildren, useEffect } from 'react';
import { SimpleHead } from 'shared/page/Head';
import { HeroBanner } from 'shared/page/warp/HeroBanner';
import { MainStatic } from 'shared/page/warp/Main';
import { Menu } from 'shared/page/warp/Menu';
import type { SeoData } from 'shared/seo/interfaces';
import type { Vertical } from '@/data/verticals';
import type { LevelFactory } from './factory';

import { Search } from './OriginDestinationSearch';
import styles from './Page.module.css';
import { StructuredData } from './StructuredData';

interface Props {
    factory: LevelFactory;
    vertical: Vertical;
    data: SeoData;
}

export const OriginDestinationPage = ({ factory, vertical, data, children }: PropsWithChildren<Props>) => {
    useEffect(() => {
        // TR_pv08 (see tracking section in README.md)
        trackPageView({
            object: {
                url: window.location.href,
                type: 'Page',
                pageType: 'travel_destination',
            },
            vertical: {
                name: 'travel',
                subVertical: vertical,
            },
        });
    }, []);

    const {
        pagePath,
        origin: { display: origin },
        destination: { display: destination, image, price },
    } = factory.pageValues();
    const canonical = `https://www.finn.no${data.basePath}${pagePath}`;
    const displayPrice = price > 0 ? `fra ${price} kr` : '';

    const insertOriginDestination = (str: string): string => str.replaceAll('$origin', origin).replaceAll('$destination', destination);

    const siteTitle = insertOriginDestination(data.lang.metaTitleOriginDestination).replace(/\$price/gm, displayPrice);
    const description = insertOriginDestination(data.lang.metaDescriptionOriginDestination);
    const productName = insertOriginDestination(data.lang.headingOriginToDestination);
    const imageUrl = `${data.cdnPath}${image}.jpg`.replace('480x360c', 'watermark-social');

    const breadcrumbList = [
        <a href="/reise/" key="breadcrumb1">
            Reise
        </a>,
        <a href={data.basePath} key="breadcrumb2">
            {data.lang.title}
        </a>,
        ...factory.breadcrumbList(),
    ];

    return (
        <>
            <SimpleHead title={siteTitle} canonical={canonical} description={description} imageUrl={imageUrl} />
            <MainStatic className={styles['seo-container']}>
                <Breadcrumbs className={`${styles['seo-wrapper']} hidden md:block my-8`}>{breadcrumbList}</Breadcrumbs>
                <div className={styles['seo-menu-wrapper']}>
                    <Menu activeVertical={vertical} />
                </div>
                <HeroBanner vertical={vertical}>
                    <Search factory={factory} vertical={vertical} />
                </HeroBanner>
                <div className={styles['seo-wrapper']}>
                    <section className="offscreen">{children}</section>
                    <section id="seo-disclaimer" className="text-s mb-64 mx-0 sm:mx-16">
                        Informasjon og data er hentet per 1. mars 2025, men endringer kan skje. Reisebransjen er i kontinuerlig utvikling,
                        og vi anbefaler derfor å dobbeltsjekke informasjonen ved bestilling og avreise. Flere land har innført
                        innreiseregistrering, så sjekk{' '}
                        <a href="https://www.regjeringen.no/no/tema/utenrikssaker/reiseinformasjon/velg-land/id2414273/">
                            UDs reiseinformasjon
                        </a>
                        .
                    </section>
                </div>
            </MainStatic>
            <StructuredData factory={factory} title={data.lang.title} productName={productName} path={data.basePath} />
        </>
    );
};
